import { assert } from '../errors';
import { FinancialEntityTypename } from '../financialEntities';
import {
    CurrentRateInfo,
    GoalTargetTimeframe,
    GoalType,
} from '../graphql/generated';
import { UserInfo } from '../react/user';
import { GoalAccountInvestmentType, Horizon } from '../types';

type RiskScoreInfo = {
    historicalReturn: number; // Floating-point rate
    composition: {
        stocksUS: number; // Floating-point fraction
        stocksIntl: number; // Floating-point fraction
        bondsUS?: number; // Floating-point fraction
        treasuryUS?: number; // Floating-point fraction
        bondsAndTreasuryUS?: number; // Floating-point fraction
    };
};

export const DEFAULT_RISK_SCORE = 5;

export const getAnnualReturnUsingRiskScore = ({
    // TODO: Try to remove this default!
    investmentType = 'DIRECT_INDEXING',
    riskScore,
    currentRateInfo,
}: {
    investmentType: GoalAccountInvestmentType | undefined;
    riskScore?: number | null | undefined;
    currentRateInfo: CurrentRateInfo;
}): number => {
    const assumedRiskScore = riskScore ?? DEFAULT_RISK_SCORE;
    return getHistoricalReturn({
        investmentType: investmentType,
        riskScore: assumedRiskScore,
        currentRateInfo,
    });
};

export const getHistoricalReturn = ({
    investmentType,
    riskScore,
    currentRateInfo,
}: {
    investmentType: GoalAccountInvestmentType;
    riskScore: number;
    currentRateInfo: CurrentRateInfo;
}): number => {
    // HIGH_YIELD_CASH
    if (investmentType === 'HIGH_YIELD_CASH') {
        assert(riskScore === 0);
        return currentRateInfo.moneyMarketRate;
    }

    // DIRECT_INDEXING / CLASSIC_PORTFOLIO
    assert(riskScore >= 1 && riskScore <= 10, 'Invalid risk score');
    const infoByRiskScore = {
        DIRECT_INDEXING: directIndexingInfoByRiskScore,
        CLASSIC_PORTFOLIO: classicPortfolioInfoByRiskScore,
    }[investmentType];
    return infoByRiskScore[riskScore].historicalReturn;
};

// Ref: https://docs.google.com/spreadsheets/d/1ojsL8X9SkiFt4XNoJbU1rKfE1LHxOOT1MWoq5Q52CXc/edit#gid=0
// N.B. The composition values may not add up to 100% due to rounding
export const directIndexingInfoByRiskScore: Record<number, RiskScoreInfo> = {
    1: {
        historicalReturn: 0.0586,
        composition: {
            stocksUS: 0.23,
            stocksIntl: 0.15,
            bondsUS: 0.38,
            treasuryUS: 0.24,
        },
    },
    2: {
        historicalReturn: 0.0602,
        composition: {
            stocksUS: 0.28,
            stocksIntl: 0.18,
            bondsUS: 0.33,
            treasuryUS: 0.21,
        },
    },
    3: {
        historicalReturn: 0.0616,
        composition: {
            stocksUS: 0.33,
            stocksIntl: 0.21,
            bondsUS: 0.29,
            treasuryUS: 0.18,
        },
    },
    4: {
        historicalReturn: 0.063,
        composition: {
            stocksUS: 0.37,
            stocksIntl: 0.23,
            bondsUS: 0.24,
            treasuryUS: 0.16,
        },
    },
    5: {
        historicalReturn: 0.0643,
        composition: {
            stocksUS: 0.41,
            stocksIntl: 0.26,
            bondsUS: 0.21,
            treasuryUS: 0.13,
        },
    },
    6: {
        historicalReturn: 0.0655,
        composition: {
            stocksUS: 0.44,
            stocksIntl: 0.28,
            bondsUS: 0.17,
            treasuryUS: 0.11,
        },
    },
    7: {
        historicalReturn: 0.0668,
        composition: {
            stocksUS: 0.48,
            stocksIntl: 0.31,
            bondsUS: 0.13,
            treasuryUS: 0.08,
        },
    },
    8: {
        historicalReturn: 0.068,
        composition: {
            stocksUS: 0.52,
            stocksIntl: 0.33,
            bondsUS: 0.09,
            treasuryUS: 0.06,
        },
    },
    9: {
        historicalReturn: 0.0692,
        composition: {
            stocksUS: 0.56,
            stocksIntl: 0.35,
            bondsUS: 0.06,
            treasuryUS: 0.04,
        },
    },
    10: {
        historicalReturn: 0.0704,
        composition: {
            stocksUS: 0.59,
            stocksIntl: 0.38,
            bondsUS: 0.02,
            treasuryUS: 0.01,
        },
    },
};

// N.B. The composition values may not add up to 100% due to rounding
export const classicPortfolioInfoByRiskScore: Record<number, RiskScoreInfo> = {
    1: {
        historicalReturn: 0.0586,
        composition: {
            stocksUS: 0.24,
            stocksIntl: 0.15,
            bondsAndTreasuryUS: 0.62,
        },
    },
    2: {
        historicalReturn: 0.0602,
        composition: {
            stocksUS: 0.28,
            stocksIntl: 0.18,
            bondsAndTreasuryUS: 0.54,
        },
    },
    3: {
        historicalReturn: 0.0616,
        composition: {
            stocksUS: 0.33,
            stocksIntl: 0.2,
            bondsAndTreasuryUS: 0.47,
        },
    },
    4: {
        historicalReturn: 0.063,
        composition: {
            stocksUS: 0.37,
            stocksIntl: 0.23,
            bondsAndTreasuryUS: 0.4,
        },
    },
    5: {
        historicalReturn: 0.0643,
        composition: {
            stocksUS: 0.41,
            stocksIntl: 0.26,
            bondsAndTreasuryUS: 0.34,
        },
    },
    6: {
        historicalReturn: 0.0655,
        composition: {
            stocksUS: 0.45,
            stocksIntl: 0.28,
            bondsAndTreasuryUS: 0.27,
        },
    },
    7: {
        historicalReturn: 0.0668,
        composition: {
            stocksUS: 0.49,
            stocksIntl: 0.3,
            bondsAndTreasuryUS: 0.21,
        },
    },
    8: {
        historicalReturn: 0.068,
        composition: {
            stocksUS: 0.52,
            stocksIntl: 0.33,
            bondsAndTreasuryUS: 0.15,
        },
    },
    9: {
        historicalReturn: 0.0692,
        composition: {
            stocksUS: 0.56,
            stocksIntl: 0.35,
            bondsAndTreasuryUS: 0.09,
        },
    },
    10: {
        historicalReturn: 0.0704,
        composition: {
            stocksUS: 0.6,
            stocksIntl: 0.37,
            bondsAndTreasuryUS: 0.03,
        },
    },
};

export const getGoalDirection = ({
    sourceFEType,
    destinationFEType,
}: {
    sourceFEType: FinancialEntityTypename;
    destinationFEType: FinancialEntityTypename;
}) =>
    sourceFEType === 'PlentyGoalAccount' &&
    destinationFEType === 'PlentyGoalAccount'
        ? 'GOAL_TO_GOAL'
        : sourceFEType === 'PlentyGoalAccount'
          ? 'WITHDRAWAL'
          : 'DEPOSIT';

const TARGET_TIME_FRAME_TO_HORIZON_MAP: Record<
    GoalTargetTimeframe,
    Horizon | 'MONEY_MARKET_FUND'
> = {
    ZERO_TO_ONE_YEARS: 'MONEY_MARKET_FUND',
    ONE_TO_TWO_YEARS: 'SHORT_TERM',
    TWO_TO_FIVE_YEARS: 'MEDIUM_TERM',
    FIVE_PLUS_YEARS: 'LONG_TERM',
};

export const getRecommendedRiskScoreUsingTargetTimeframe = ({
    goalType,
    targetTimeframe,
    userInfo,
}: {
    goalType: GoalType;
    targetTimeframe: GoalTargetTimeframe | undefined | null;
    userInfo: UserInfo;
}): number | undefined => {
    const { recommendedRiskScoreByHorizon } = userInfo;

    // On web, we use goal configs for this short-circuit, but since there are only 2 exceptions, this seems simpler
    if (goalType === 'CASH_MANAGEMENT' || goalType === 'EMERGENCY_FUND') {
        return 0;
    }

    if (recommendedRiskScoreByHorizon && targetTimeframe) {
        const horizon = TARGET_TIME_FRAME_TO_HORIZON_MAP[targetTimeframe];
        return horizon === 'MONEY_MARKET_FUND'
            ? 0
            : recommendedRiskScoreByHorizon[horizon];
    }
};
